import { getCountryById } from './countrySettings';

const COUNTRY_CODE_TO_ID = {
  ie: 'ie',
  gb: 'uk',
  uk: 'uk',
  us: 'us',
  ca: 'us' // Default Canadian users to US setup for now
};

const mapCountryCodeToId = (countryCode) => {
  const normalized = String(countryCode || '').trim().toLowerCase();
  if (!normalized) return null;
  return COUNTRY_CODE_TO_ID[normalized] || null;
};

const detectCountryFromBrowserSignals = () => {
  try {
    const timezone = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || '';
    if (/^Europe\/London$/i.test(timezone)) return 'uk';
    if (/^Europe\/Dublin$/i.test(timezone)) return 'ie';
    if (/^America\//i.test(timezone)) return 'us';
  } catch {
    // Ignore timezone detection errors and continue to locale hints.
  }

  if (typeof navigator !== 'undefined') {
    const localeCandidates = [
      navigator.language,
      ...(Array.isArray(navigator.languages) ? navigator.languages : [])
    ]
      .map((entry) => String(entry || '').trim())
      .filter(Boolean);

    for (const locale of localeCandidates) {
      const regionMatch = locale.match(/[-_]([A-Za-z]{2})\b/);
      const region = regionMatch?.[1] ? regionMatch[1].toLowerCase() : '';
      const mapped = mapCountryCodeToId(region);
      if (mapped) return mapped;
    }
  }

  return null;
};

/**
 * Detect user's country from browser hints and optional IP geolocation fallback.
 * @returns {Promise<string|null>} Country ID (e.g., 'ie', 'uk', 'us') or null when unknown.
 */
export const detectCountryFromIP = async () => {
  const browserHint = detectCountryFromBrowserSignals();
  if (browserHint) {
    console.log(`Auto-detected country from browser settings: ${browserHint}`);
    return browserHint;
  }

  try {
    // Fallback to IP geolocation if browser hints are unavailable.
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 3500) : null;

    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller?.signal
    });

    if (timeoutId) clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      const countryCode = String(data.country_code || '').toLowerCase();
      const mappedCountry = mapCountryCodeToId(countryCode);
      if (mappedCountry) {
        console.log(`Auto-detected country from IP: ${countryCode} -> ${mappedCountry}`);
        return mappedCountry;
      }
    }
  } catch (error) {
    console.warn('Could not auto-detect country from IP:', error);
  }

  console.log('Country auto-detect unavailable; keeping current/default selection');
  return null;
};

/**
 * Get the current country settings from localStorage or default.
 * @returns {Object} The country object
 */
export const getCurrentCountry = () => {
  const savedCountryId = localStorage.getItem('selectedCountry');
  return getCountryById(savedCountryId || 'ie');
};

/**
 * Get the appropriate tax rates for the current country
 * @returns {Array} Array of tax rates
 */
export const getCurrentTaxRates = () => {
  const country = getCurrentCountry();
  if (country.taxSystem === 'vat') {
    return country.vatRates;
  }
  return country.salesTaxRates || [];
};

/**
 * Get the default tax rate for the current country
 * @returns {number} The default tax rate
 */
export const getDefaultTaxRate = () => {
  const country = getCurrentCountry();
  if (country.taxSystem === 'vat') {
    return country.defaultVatRate;
  }
  return country.defaultSalesTax || 0;
};

/**
 * Format a tax rate for display
 * @param {number} rate - The tax rate
 * @param {string} countryId - The country ID (optional)
 * @returns {string} The formatted tax rate
 */
export const formatTaxRate = (rate, countryId) => {
  const country = countryId ? getCountryById(countryId) : getCurrentCountry();
  const taxSystem = country.taxSystem;

  if (rate === 0) {
    return taxSystem === 'vat' ? 'Zero Rate (0%)' : 'No Tax (0%)';
  }

  return `${taxSystem === 'vat' ? 'VAT' : 'Sales Tax'} ${rate}%`;
};
